import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  Plus,
  Trash2,
  ClipboardList,
  AlertCircle,
  User,
  CheckCircle2,
  XCircle,
  FileSignature,
  Package,
  Edit2,
  UserCheck,
  Building2,
  Shield,
  RotateCcw,
  Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn, formatDate, formatDateTime } from '@/lib/utils';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';
import AddAssetModal from '../components/AddAssetModal';
import SignatureModal from '../components/SignatureModal';
import ReturnAssetModal from '../components/ReturnAssetModal';

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  employeeId: string;
  department?: { name: string };
}

interface Asset {
  id: string;
  name: string;
  assetTag: string;
  serialNumber?: string;
  category?: { name: string };
  brand?: { name: string };
}

interface AssetIssueItem {
  id: string;
  assetId: string;
  asset: Asset;
  condition: string;
  accessories?: string;
  notes?: string;
  issuedAt?: string;
  returnedAt?: string;
  returnCondition?: string;
}

interface AssetIssueForm {
  id: string;
  formNumber: string;
  employeeId: string;
  employee?: Employee;
  purpose: string;
  notes?: string;
  status: string;
  employeeSignature?: string;
  employeeSignedAt?: string;
  issuerSignature?: string;
  issuerSignedAt?: string;
  issuedById?: string;
  issuedBy?: { firstName: string; lastName: string };
  lineManagerId?: string;
  lineManagerName?: string;
  lineManagerSignature?: string;
  lineManagerSignedAt?: string;
  lineManagerApproved?: boolean;
  hodItId?: string;
  hodItName?: string;
  hodItSignature?: string;
  hodItSignedAt?: string;
  hodItApproved?: boolean;
  hodItComments?: string;
  rejectionReason?: string;
  items: AssetIssueItem[];
  createdAt: string;
  updatedAt: string;
}

function getFormStatusColor(status: string): string {
  const colors: Record<string, string> = {
    DRAFT: 'bg-gray-100 text-gray-800',
    PENDING_MANAGER: 'bg-yellow-100 text-yellow-800',
    PENDING_HOD: 'bg-orange-100 text-orange-800',
    APPROVED: 'bg-blue-100 text-blue-800',
    ISSUED: 'bg-green-100 text-green-800',
    RETURNED: 'bg-purple-100 text-purple-800',
    CANCELLED: 'bg-gray-100 text-gray-800',
    REJECTED: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

function getItemStatusColor(item: AssetIssueItem): string {
  if (item.returnedAt) return 'bg-purple-100 text-purple-800';
  if (item.issuedAt) return 'bg-green-100 text-green-800';
  return 'bg-yellow-100 text-yellow-800';
}

function getItemStatus(item: AssetIssueItem): string {
  if (item.returnedAt) return 'Returned';
  if (item.issuedAt) return 'Issued';
  return 'Pending';
}

export default function AssetIssueFormDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const [showAddAssetModal, setShowAddAssetModal] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureType, setSignatureType] = useState<'employee' | 'issuer' | 'manager' | 'hod'>('employee');
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<AssetIssueItem | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({ purpose: '', notes: '' });

  // Fetch form details
  const { data: form, isLoading, error, refetch } = useQuery({
    queryKey: ['asset-issue-form', id],
    queryFn: async () => {
      const response = await api.get(`/asset-issue-forms/${id}`);
      return response.data.data as AssetIssueForm;
    },
    enabled: Boolean(id),
  });

  // Update form mutation
  const updateMutation = useMutation({
    mutationFn: async (data: { purpose: string; notes: string }) => {
      const response = await api.put(`/asset-issue-forms/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-issue-form', id] });
      setIsEditing(false);
      toast({ title: 'Success', description: 'Form updated successfully' });
    },
    onError: (error: any) => {
      setSubmitError(error.response?.data?.message || 'Failed to update form');
    },
  });

  // Remove asset mutation
  const removeAssetMutation = useMutation({
    mutationFn: async (itemId: string) => {
      const response = await api.delete(`/asset-issue-forms/${id}/assets/${itemId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-issue-form', id] });
      toast({ title: 'Success', description: 'Asset removed from form' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.message || 'Failed to remove asset',
        variant: 'destructive'
      });
    },
  });

  // Sign mutations
  const signEmployeeMutation = useMutation({
    mutationFn: async (signature: string) => {
      const response = await api.post(`/asset-issue-forms/${id}/sign-employee`, { signature });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-issue-form', id] });
      toast({ title: 'Success', description: 'Employee signature recorded' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.message || 'Failed to sign',
        variant: 'destructive'
      });
    },
  });

  const signIssuerMutation = useMutation({
    mutationFn: async (signature: string) => {
      const response = await api.post(`/asset-issue-forms/${id}/sign-issuer`, { signature });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-issue-form', id] });
      toast({ title: 'Success', description: 'Issuer signature recorded' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.message || 'Failed to sign',
        variant: 'destructive'
      });
    },
  });

  const signManagerMutation = useMutation({
    mutationFn: async (data: { signature: string; lineManagerName: string; approved: boolean }) => {
      const response = await api.post(`/asset-issue-forms/${id}/sign-manager`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-issue-form', id] });
      toast({ title: 'Success', description: 'Line Manager signature recorded' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.message || 'Failed to sign',
        variant: 'destructive'
      });
    },
  });

  const approveHodMutation = useMutation({
    mutationFn: async (data: { signature: string; hodItName: string; approved: boolean; comments?: string }) => {
      const response = await api.post(`/asset-issue-forms/${id}/approve-hod`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-issue-form', id] });
      toast({ title: 'Success', description: 'Head of IT approval recorded' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.message || 'Failed to approve',
        variant: 'destructive'
      });
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: async (reason: string) => {
      const response = await api.post(`/asset-issue-forms/${id}/reject`, { reason });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-issue-form', id] });
      toast({ title: 'Form Rejected', description: 'The form has been rejected' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.message || 'Failed to reject',
        variant: 'destructive'
      });
    },
  });

  // Issue assets mutation
  const issueMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/asset-issue-forms/${id}/issue`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-issue-form', id] });
      toast({ title: 'Success', description: 'Assets have been issued' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.message || 'Failed to issue assets',
        variant: 'destructive'
      });
    },
  });

  // Return asset mutation
  const returnAssetMutation = useMutation({
    mutationFn: async (data: { itemId: string; returnCondition: string; notes?: string }) => {
      const response = await api.post(`/asset-issue-forms/${id}/return/${data.itemId}`, {
        returnCondition: data.returnCondition,
        notes: data.notes,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-issue-form', id] });
      setShowReturnModal(false);
      setSelectedItem(null);
      toast({ title: 'Success', description: 'Asset returned successfully' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.message || 'Failed to return asset',
        variant: 'destructive'
      });
    },
  });

  const handleOpenSignature = (type: 'employee' | 'issuer' | 'manager' | 'hod') => {
    setSignatureType(type);
    setShowSignatureModal(true);
  };

  const handleSignature = async (data: { signature: string; name?: string; approved?: boolean; comments?: string }) => {
    switch (signatureType) {
      case 'employee':
        await signEmployeeMutation.mutateAsync(data.signature);
        break;
      case 'issuer':
        await signIssuerMutation.mutateAsync(data.signature);
        break;
      case 'manager':
        await signManagerMutation.mutateAsync({
          signature: data.signature,
          lineManagerName: data.name || '',
          approved: data.approved ?? true,
        });
        break;
      case 'hod':
        await approveHodMutation.mutateAsync({
          signature: data.signature,
          hodItName: data.name || '',
          approved: data.approved ?? true,
          comments: data.comments,
        });
        break;
    }
    setShowSignatureModal(false);
  };

  const handleReturnAsset = (item: AssetIssueItem) => {
    setSelectedItem(item);
    setShowReturnModal(true);
  };

  const startEditing = () => {
    if (form) {
      setEditData({ purpose: form.purpose || '', notes: form.notes || '' });
      setIsEditing(true);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !form) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-destructive mb-4" />
        <p className="text-lg font-medium">Form not found</p>
        <Button variant="link" onClick={() => navigate('/asset-issue-forms')}>
          Back to forms
        </Button>
      </div>
    );
  }

  const isDraft = form.status === 'DRAFT';
  const canEdit = isDraft;
  const canAddAssets = isDraft;
  const canSignEmployee = isDraft && !form.employeeSignedAt && form.items.length > 0;
  const canSignIssuer = isDraft && form.employeeSignedAt && !form.issuerSignedAt;
  const canSignManager = form.status === 'PENDING_MANAGER' && !form.lineManagerSignedAt;
  const canApproveHod = form.status === 'PENDING_HOD' && !form.hodItSignedAt;
  const canIssue = form.status === 'APPROVED';
  const canReturn = form.status === 'ISSUED';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/asset-issue-forms')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <ClipboardList className="w-8 h-8" />
              {form.formNumber}
            </h1>
            <p className="text-muted-foreground">Asset Issue Form</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={cn("px-3 py-1 text-sm font-medium rounded-full", getFormStatusColor(form.status))}>
            {form.status.replace(/_/g, ' ')}
          </span>
        </div>
      </div>

      {/* Error Alert */}
      {submitError && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 border border-destructive/20">
          <AlertCircle className="w-5 h-5 text-destructive" />
          <p className="text-sm text-destructive">{submitError}</p>
          <Button variant="ghost" size="sm" onClick={() => setSubmitError(null)}>
            <XCircle className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Rejection Reason */}
      {form.status === 'REJECTED' && form.rejectionReason && (
        <Card className="border-destructive">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-destructive mt-0.5" />
              <div>
                <p className="font-medium text-destructive">Form Rejected</p>
                <p className="text-sm text-muted-foreground">{form.rejectionReason}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Form Details */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Form Details</CardTitle>
                <CardDescription>Asset issuance information</CardDescription>
              </div>
              {canEdit && !isEditing && (
                <Button variant="outline" size="sm" onClick={startEditing}>
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {isEditing ? (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Purpose</label>
                    <textarea
                      className="w-full mt-1 p-3 rounded-md border border-input bg-background text-sm min-h-[80px]"
                      value={editData.purpose}
                      onChange={(e) => setEditData({ ...editData, purpose: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Notes</label>
                    <textarea
                      className="w-full mt-1 p-3 rounded-md border border-input bg-background text-sm min-h-[80px]"
                      value={editData.notes}
                      onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => updateMutation.mutate(editData)} disabled={updateMutation.isPending}>
                      {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">Employee</p>
                      {form.employee ? (
                        <>
                          <p className="font-medium">{form.employee.firstName} {form.employee.lastName}</p>
                          <p className="text-sm text-muted-foreground">{form.employee.employeeId}</p>
                          {form.employee.department && (
                            <p className="text-sm text-muted-foreground">{form.employee.department.name}</p>
                          )}
                        </>
                      ) : (
                        <p>-</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">Created</p>
                      <p className="font-medium">{formatDateTime(form.createdAt)}</p>
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-sm text-muted-foreground mb-1">Purpose</p>
                    <p>{form.purpose || '-'}</p>
                  </div>
                  {form.notes && (
                    <div className="sm:col-span-2">
                      <p className="text-sm text-muted-foreground mb-1">Notes</p>
                      <p>{form.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Assets List */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Assets
                </CardTitle>
                <CardDescription>{form.items.length} asset(s) on this form</CardDescription>
              </div>
              {canAddAssets && (
                <Button onClick={() => setShowAddAssetModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Asset
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {form.items.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No assets added yet</p>
                  {canAddAssets && (
                    <Button variant="link" onClick={() => setShowAddAssetModal(true)}>
                      Add your first asset
                    </Button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-4 font-medium">Asset Tag</th>
                        <th className="text-left p-4 font-medium">Name</th>
                        <th className="text-left p-4 font-medium">Serial Number</th>
                        <th className="text-left p-4 font-medium">Condition</th>
                        <th className="text-left p-4 font-medium">Status</th>
                        <th className="text-right p-4 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {form.items.map((item) => (
                        <tr key={item.id} className="hover:bg-muted/50">
                          <td className="p-4">
                            <span className="font-mono text-sm font-medium text-primary">
                              {item.asset.assetTag}
                            </span>
                          </td>
                          <td className="p-4">
                            <div>
                              <p className="font-medium">{item.asset.name}</p>
                              {item.asset.category && (
                                <p className="text-sm text-muted-foreground">{item.asset.category.name}</p>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="font-mono text-sm">{item.asset.serialNumber || '-'}</span>
                          </td>
                          <td className="p-4">
                            <span className="text-sm">{item.condition}</span>
                            {item.returnCondition && (
                              <p className="text-xs text-muted-foreground">Return: {item.returnCondition}</p>
                            )}
                          </td>
                          <td className="p-4">
                            <span className={cn("px-2 py-1 text-xs font-medium rounded-full", getItemStatusColor(item))}>
                              {getItemStatus(item)}
                            </span>
                            {item.issuedAt && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatDate(item.issuedAt)}
                              </p>
                            )}
                          </td>
                          <td className="p-4">
                            <div className="flex items-center justify-end gap-1">
                              {canReturn && item.issuedAt && !item.returnedAt && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleReturnAsset(item)}
                                >
                                  <RotateCcw className="w-4 h-4 mr-1" />
                                  Return
                                </Button>
                              )}
                              {canAddAssets && (
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => removeAssetMutation.mutate(item.id)}
                                >
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Signature Workflow */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSignature className="w-5 h-5" />
                Signatures
              </CardTitle>
              <CardDescription>Approval workflow status</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Employee Signature */}
              <div className={cn("p-3 rounded-lg border", form.employeeSignedAt ? "bg-green-50 border-green-200" : "bg-muted/50")}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span className="font-medium text-sm">Employee</span>
                  </div>
                  {form.employeeSignedAt ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <span className="text-xs text-muted-foreground">Pending</span>
                  )}
                </div>
                {form.employeeSignedAt && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    <p>Signed: {formatDateTime(form.employeeSignedAt)}</p>
                    {form.employeeSignature && <p className="italic mt-1">"{form.employeeSignature}"</p>}
                  </div>
                )}
                {canSignEmployee && (
                  <Button 
                    className="w-full mt-2" 
                    size="sm"
                    onClick={() => handleOpenSignature('employee')}
                  >
                    Sign as Employee
                  </Button>
                )}
              </div>

              {/* Issuer Signature */}
              <div className={cn("p-3 rounded-lg border", form.issuerSignedAt ? "bg-green-50 border-green-200" : "bg-muted/50")}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UserCheck className="w-4 h-4" />
                    <span className="font-medium text-sm">Issuer (Admin)</span>
                  </div>
                  {form.issuerSignedAt ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <span className="text-xs text-muted-foreground">Pending</span>
                  )}
                </div>
                {form.issuerSignedAt && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    <p>Signed: {formatDateTime(form.issuerSignedAt)}</p>
                    {form.issuedBy && <p>By: {form.issuedBy.firstName} {form.issuedBy.lastName}</p>}
                  </div>
                )}
                {canSignIssuer && (
                  <Button 
                    className="w-full mt-2" 
                    size="sm"
                    onClick={() => handleOpenSignature('issuer')}
                  >
                    Sign as Issuer
                  </Button>
                )}
              </div>

              {/* Line Manager Signature */}
              <div className={cn("p-3 rounded-lg border", form.lineManagerSignedAt ? "bg-green-50 border-green-200" : "bg-muted/50")}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    <span className="font-medium text-sm">Line Manager</span>
                  </div>
                  {form.lineManagerSignedAt ? (
                    form.lineManagerApproved ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )
                  ) : (
                    <span className="text-xs text-muted-foreground">Pending</span>
                  )}
                </div>
                {form.lineManagerSignedAt && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    <p>Signed: {formatDateTime(form.lineManagerSignedAt)}</p>
                    {form.lineManagerName && <p>By: {form.lineManagerName}</p>}
                  </div>
                )}
                {canSignManager && (
                  <Button 
                    className="w-full mt-2" 
                    size="sm"
                    onClick={() => handleOpenSignature('manager')}
                  >
                    Sign as Manager
                  </Button>
                )}
              </div>

              {/* Head of IT Approval */}
              <div className={cn("p-3 rounded-lg border", form.hodItSignedAt ? "bg-green-50 border-green-200" : "bg-muted/50")}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    <span className="font-medium text-sm">Head of IT</span>
                  </div>
                  {form.hodItSignedAt ? (
                    form.hodItApproved ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )
                  ) : (
                    <span className="text-xs text-muted-foreground">Pending</span>
                  )}
                </div>
                {form.hodItSignedAt && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    <p>Signed: {formatDateTime(form.hodItSignedAt)}</p>
                    {form.hodItName && <p>By: {form.hodItName}</p>}
                    {form.hodItComments && <p className="italic mt-1">"{form.hodItComments}"</p>}
                  </div>
                )}
                {canApproveHod && (
                  <Button 
                    className="w-full mt-2" 
                    size="sm"
                    onClick={() => handleOpenSignature('hod')}
                  >
                    Approve as HOD
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {canIssue && (
                <Button 
                  className="w-full" 
                  onClick={() => issueMutation.mutate()}
                  disabled={issueMutation.isPending}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {issueMutation.isPending ? 'Issuing...' : 'Issue Assets'}
                </Button>
              )}
              
              {(canSignManager || canApproveHod) && (
                <Button 
                  variant="destructive" 
                  className="w-full"
                  onClick={() => {
                    const reason = prompt('Please enter a rejection reason:');
                    if (reason) {
                      rejectMutation.mutate(reason);
                    }
                  }}
                  disabled={rejectMutation.isPending}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  {rejectMutation.isPending ? 'Rejecting...' : 'Reject Form'}
                </Button>
              )}

              <Button 
                variant="outline" 
                className="w-full" 
                onClick={() => navigate('/asset-issue-forms')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to List
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modals */}
      <AddAssetModal
        open={showAddAssetModal}
        onClose={() => setShowAddAssetModal(false)}
        formId={id!}
        onSuccess={() => {
          setShowAddAssetModal(false);
          refetch();
        }}
      />

      <SignatureModal
        open={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        type={signatureType}
        onSign={handleSignature}
      />

      <ReturnAssetModal
        open={showReturnModal}
        onClose={() => {
          setShowReturnModal(false);
          setSelectedItem(null);
        }}
        item={selectedItem}
        onReturn={(data) => {
          if (selectedItem) {
            returnAssetMutation.mutate({
              itemId: selectedItem.id,
              returnCondition: data.returnCondition,
              notes: data.notes,
            });
          }
        }}
        isLoading={returnAssetMutation.isPending}
      />
    </div>
  );
}
